import { z } from 'zod';

/**
 * Ghost text data schema for QuickFind search input placeholder.
 * Used to persist and restore dynamic placeholder text across sessions.
 */
export const ghostTextDataSchema = z.object({
	/** The placeholder text to display */
	text: z.string(),
	/** ISO timestamp when this ghost text expires */
	expire: z.string(),
	/** Category/context for the ghost text */
	category: z.enum(['general', 'search']),
});

export type GhostTextData = z.infer<typeof ghostTextDataSchema>;

/**
 * Default expiry duration for ghost text (24 hours in milliseconds)
 */
export const DEFAULT_EXPIRY_MS = 24 * 60 * 60 * 1000;

/**
 * Parses a JSON string into ghost text data.
 * Returns undefined if parsing fails or data is invalid.
 *
 * @param json - The JSON string to parse
 * @returns GhostTextData object or undefined if invalid
 */
export const parseGhostText = (json: string): GhostTextData | undefined => {
	if (!json) {
		return undefined;
	}

	try {
		const result = ghostTextDataSchema.safeParse(JSON.parse(json));

		if (result.success) {
			const data = result.data;
			if (new Date(data.expire).getTime() < Date.now()) {
				return undefined;
			}
			return data;
		}

		return undefined;
	} catch {
		return undefined;
	}
};
